// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import Subheading from '../../../../theme/text/Subheading';
import SelectGroup from '../../../../theme/Select/SelectGroup';
import NumberInput from '../../../../theme/inputs/NumberInput';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, InputBase, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { tokens } from '../../../../../theme';
import { useDispatch, useSelector } from 'react-redux';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import * as yup from "yup";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { yupRequired } from '../../../../../utils/validation';
import cx from "classnames"
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import EMGeneralExplainerBox from '../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import Shbutton from '../../../../theme/buttons/Shbutton';
import { useTestWebshopConnectionMutation } from '../../../../../newapi/webshop/webshopSlice';
import { useGetSubscriptionPricesQuery } from '../../../../../newapi/subscription/subscriptionSlice';
import ShLabel from '../../../../theme/label/ShLabel';
import AmazonOAuthButton from '../components/AmazonOAuthButton';
import AmazonSubscriptionInfo from '../components/AmazonSubscriptionInfo';
import TikTokOAuthButton from '../components/TikTokOAuthButton';
import { pickTikTokShop, buildTikTokCredentials } from '../components/tiktokOauth';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import { config } from '../../../../../newapi/apiUrlConfig';
import { kaufland_storefronts, mirakl_marketplaces, findMiraklMarketplaceIndex } from '../../../../../utils/staticEnums';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
// Explainers shown next to specific credential fields, keyed by webshop_type -> field key.
// Add entries here to surface an info tooltip for a given webshop's credential input.
const CREDENTIAL_FIELD_EXPLAINERS = {
    mirakl: {
        url: { title: 'em_mirakl_url_title', text: 'em_mirakl_url_text' },
    },
};

// TikTok partner apps are region-locked; the US region requires its own partner
// app and is rejected by the backend, so only Global is offered here.
const TIKTOK_REGION_OPTIONS = [
    { value: 'global', label: 'Global' },
];

// Credential fields populated by the TikTok OAuth flow, never typed by a user.
const TIKTOK_OAUTH_MANAGED_FIELDS = [
    'access_token',
    'refresh_token',
    'expiresAt',
    'shop_cipher',
    'shop_id',
    'seller_name',
    'region',
];

const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const [webshopCredentialsInfo, setWebshopCredentialsInfo] = useState(data.credentials);
    const [isOAuthAuthenticated, setIsOAuthAuthenticated] = useState(false);
    const [oauthError, setOauthError] = useState(null);
    // Mirakl: true when the marketplace is not in mirakl_marketplaces and the URL is typed manually.
    const [miraklOther, setMiraklOther] = useState(!!data.credentials?.url && findMiraklMarketplaceIndex(data.credentials.url) === -1);
    const miraklOptions = [...mirakl_marketplaces, { value: 'other', label: t('other') }];

    const handleCredentialsChange = (key, val) => {
        setWebshopCredentialsInfo({
            ...webshopCredentialsInfo,
            [key]: val
        });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    }

    useEffect(() => {
        props.handleDataUpdate({ credentials: webshopCredentialsInfo });
    }, [webshopCredentialsInfo]);

    const [tiktokOAuthPayload, setTiktokOAuthPayload] = useState(null);

    useEffect(() => {
        if (data.webshop_type === 'amazon_stockhub') {
            setIsOAuthAuthenticated(!!webshopCredentialsInfo?.refresh_token);
        }
    }, [data.webshop_type, webshopCredentialsInfo?.refresh_token]);

    useEffect(() => {
        if (data.webshop_type === 'tiktokshop') {
            setIsOAuthAuthenticated(!!webshopCredentialsInfo?.access_token || !!tiktokOAuthPayload);
        }
    }, [data.webshop_type, webshopCredentialsInfo?.access_token, tiktokOAuthPayload]);

    const handleAmazonOAuthSuccess = ({ refresh_token, selling_partner_id }) => {
        setWebshopCredentialsInfo({
            ...webshopCredentialsInfo,
            refresh_token,
            selling_partner_id,
        });
        setIsOAuthAuthenticated(true);
        setOauthError(null);
    };

    const handleAmazonOAuthError = (error) => {
        setOauthError(error);
        setIsOAuthAuthenticated(false);
    };

    const handleAmazonOAuthDisconnect = () => {
        const {
            refresh_token,
            selling_partner_id,
            marketplace,
            marketplace_id,
            ...remainingCredentials
        } = webshopCredentialsInfo;

        setWebshopCredentialsInfo(remainingCredentials);
        setIsOAuthAuthenticated(false);
        setOauthError(null);
    };

    const handleTikTokOAuthSuccess = (payload) => {
        if (!payload?.access_token || !payload?.refresh_token || !payload?.expiresAt
            || !Array.isArray(payload.shops) || payload.shops.length === 0) {
            setOauthError(t('tiktok_connection_failed'));
            return;
        }
        setOauthError(null);
        const shop = pickTikTokShop(payload.shops);
        if (shop) {
            setWebshopCredentialsInfo({
                ...webshopCredentialsInfo,
                ...buildTikTokCredentials(payload, shop),
            });
            setTiktokOAuthPayload(null);
        } else {
            // Multiple authorized shops: keep the payload so the shop picker renders.
            setTiktokOAuthPayload(payload);
        }
    };

    const handleTikTokShopPick = (selected) => {
        const shop = tiktokOAuthPayload?.shops?.[selected];
        const credentials = buildTikTokCredentials(tiktokOAuthPayload, shop);
        if (credentials) {
            setWebshopCredentialsInfo({
                ...webshopCredentialsInfo,
                ...credentials,
            });
            // Same as the single-shop path: the payload has served its purpose, and
            // clearing it closes the picker and lets authentication derive from the token.
            setTiktokOAuthPayload(null);
            setOauthError(null);
        } else {
            // A shop without a usable cipher cannot be connected; surface it rather
            // than silently ignoring the pick and leaving the user stuck.
            setOauthError(t('tiktok_connection_failed'));
        }
    };

    const handleTikTokOAuthError = (error) => {
        setOauthError(error);
        setIsOAuthAuthenticated(false);
    };

    const handleTikTokOAuthDisconnect = () => {
        const {
            access_token,
            refresh_token,
            expiresAt,
            shop_cipher,
            shop_id,
            seller_name,
            ...remainingCredentials
        } = webshopCredentialsInfo;

        setWebshopCredentialsInfo(remainingCredentials);
        setTiktokOAuthPayload(null);
        setIsOAuthAuthenticated(false);
        setOauthError(null);
    };

    const amazonOAuthUrl = `${config.url.API_BACKEND_URL}oauth/amazon/start`;
    const amazonBackendOrigin = new URL(config.url.API_BACKEND_URL).origin;

    const tiktokOAuthUrl = `${config.url.API_BACKEND_URL}oauth/tiktok/start`;
    const tiktokBackendOrigin = new URL(config.url.API_BACKEND_URL).origin;

    const [connectionTestResult, setConnectionTestResult] = useState(null);

    const [testConnection, {isLoading: testConnectionLoading}] = useTestWebshopConnectionMutation();
    const handleTestConnection = async() => {
        if(testConnectionLoading) return;
        try {
            const response = await testConnection(data).unwrap();
            setConnectionTestResult(response.success);
        } catch (error) {
            console.error("Error testing connection:", error);
        }
    }

    // Fetch relations using the useGetRelationsQuery hook
    const { data: relationsData, isLoading: relationsLoading } = useGetRelationsSelectQuery();

    return (
        <div>
            {isFulfilment ? 
            <Box 
                sx={{
                    width:1,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    gap:7
                }}
            >
                
                <Box
                    sx={{
                        width:120,
                    }}
                >
                    <Text>{t("relation")}</Text>
                </Box>
                
                <Box
                    sx={{
                        flex:1,
                        backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                        borderRadius:4,
                        display:"flex"
                    }}
                >
                     <ShValidatedInputDropdown
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_relation_placeholder")
                            }}
                            name="relationId"
                            height={40}
                            displayName="name"
                            changeField="id"
                            selected={relationsData?.findIndex(relation => relation.id === data?.relationId) ?? 0}
                            noSelection={t("choose_relation")} //Custom text when nothing is selected
                            options={relationsData ?? []}
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    
                </Box>
            </Box>
             : 
             <></>}
             <Box 
                 sx={{
                     width:1,
                     display:"flex",
                     alignItems:"center",
                     justifyContent:"space-between",
                     gap:7,
                     mb:1.5,
                     ...(isFulfilment ? {paddingTop: 1.5} : {})
                 }}
             >
                 
                 <Box
                     sx={{
                         width:120,
                     }}
                 >
                     <Text>{t("description")}</Text>
                 </Box>
                 
                 <Box
                     sx={{
                         flex:1,
                         backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                         borderRadius:4,
                         display:"flex"
                     }}
                 >
                     <ShValidatedInput
                             name="description"
                             value={data.description ?? ""}
                             onChange={handleInput}
                             error={props.errors.description}
                         />
                     
                 </Box>
             </Box>
            <Box 
                sx={{
                    width:1,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    gap:7,
                    ...(isFulfilment ? {paddingTop: 1.5} : {})
                }}
            >
                
                <Box
                    sx={{
                        width:120,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Text>{t("shipping_name")}</Text>
                        <EMGeneralExplainerBox
                            title={t('shipping_name')}
                            text={t('em_shippingname_text')}
                        />
                </Box>
                
                <Box
                    sx={{
                        flex:1,
                        backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                        borderRadius:4,
                        display:"flex"
                    }}
                >
                    <ShValidatedInput
                            name="shippingName"
                            value={data.shippingName ?? ""}
                            onChange={handleInput}
                            error={props.errors.shippingName}
                        />
                    
                </Box>
            </Box>

            {/* Kaufland Storefront Dropdown */}
            {data.webshop_type === 'kaufland' && (
                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7,
                        mt: 2
                    }}
                >
                    <Box sx={{ width: 120 }}>
                        <Text>{t("storefront")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4,
                            display: "flex"
                        }}
                    >
                        <InputDropdown
                            name="storefront"
                            noSelection={t('choose_option')}
                            displayName="label"
                            options={kaufland_storefronts}
                            selected={kaufland_storefronts.findIndex(option => option.value === (webshopCredentialsInfo?.storefront || ''))}
                            onChange={(selected) => {
                                handleCredentialsChange('storefront', kaufland_storefronts[selected].value);
                            }}
                            closeOnSelection
                        />
                    </Box>
                </Box>
            )}

            {/* Mirakl Marketplace Dropdown: known marketplaces fill in the API URL */}
            {data.webshop_type === 'mirakl' && (
                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7,
                        mt: 2
                    }}
                >
                    <Box sx={{ width: 120 }}>
                        <Text>{t("marketplace")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4,
                            display: "flex"
                        }}
                    >
                        <ShValidatedInputDropdown
                            name="marketplace"
                            noSelection={t('choose_option')}
                            displayName="label"
                            options={miraklOptions}
                            selectIndex
                            selected={miraklOther ? miraklOptions.length - 1 : findMiraklMarketplaceIndex(webshopCredentialsInfo?.url)}
                            onChange={(name, selected) => {
                                const isOther = miraklOptions[selected].value === 'other';
                                setMiraklOther(isOther);
                                handleCredentialsChange('url', isOther ? '' : miraklOptions[selected].value);
                            }}
                            error={miraklOther ? undefined : props.errors['credentials.url']}
                            closeOnSelection
                        />
                    </Box>
                </Box>
            )}

            {/* Amazon OAuth Button for amazon_stockhub type */}
            {data.webshop_type === 'amazon_stockhub' && (
                <Box sx={{ width: 1, mt: 2 }}>
                    <AmazonOAuthButton
                        oauthUrl={amazonOAuthUrl}
                        backendOrigin={amazonBackendOrigin}
                        onSuccess={handleAmazonOAuthSuccess}
                        onError={handleAmazonOAuthError}
                        isAuthenticated={isOAuthAuthenticated}
                        onDisconnect={handleAmazonOAuthDisconnect}
                    />

                    {/* Display OAuth error if any */}
                    {oauthError && (
                        <Box sx={{
                            mt: 1.5,
                            p: 1.5,
                            backgroundColor: '#ffebee',
                            borderRadius: 1,
                            border: '1px solid #f44336'
                        }}>
                            <Text variant="body2" color="error">
                                {oauthError}
                            </Text>
                        </Box>
                    )}

                    {/* Amazon Marketplace Dropdown */}
                    <Box
                        sx={{
                            width: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 7,
                            mt: 2
                        }}
                    >
                        <Box sx={{ width: 120 }}>
                            <Text>{t("marketplace")}</Text>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                borderRadius: 4,
                                display: "flex"
                            }}
                        >
                            <InputDropdown
                                name="marketplace"
                                noSelection={t('choose_option')}
                                displayName="label"
                                options={[
                                    { value: 'NL', label: 'Netherlands (NL)', marketplaceId: 'A1805IZSGTT6HS' },
                                    { value: 'BE', label: 'Belgium (BE)', marketplaceId: 'AMEN7PMS3EDWL' },
                                    { value: 'DE', label: 'Germany (DE)', marketplaceId: 'A1PA6795UKMFR9' },
                                    { value: 'FR', label: 'France (FR)', marketplaceId: 'A13V1IB3VIYBER' },
                                    { value: 'IT', label: 'Italy (IT)', marketplaceId: 'APJ6JRA9NG5V4' },
                                    { value: 'ES', label: 'Spain (ES)', marketplaceId: 'A1RKKUPIHCS9HS' },
                                    { value: 'UK', label: 'United Kingdom (UK)', marketplaceId: 'A1F83G8C2ARO7P' },
                                    { value: 'SE', label: 'Sweden (SE)', marketplaceId: 'A2NODRKZP88ZB9' },
                                    { value: 'PL', label: 'Poland (PL)', marketplaceId: 'A1C3SOZRARQ6R3' },
                                    { value: 'IE', label: 'Ireland (IE)', marketplaceId: 'A28R8C7NBKEWEA' }
                                ]}
                                selected={[
                                    { value: 'NL', label: 'Netherlands (NL)', marketplaceId: 'A1805IZSGTT6HS' },
                                    { value: 'BE', label: 'Belgium (BE)', marketplaceId: 'AMEN7PMS3EDWL' },
                                    { value: 'DE', label: 'Germany (DE)', marketplaceId: 'A1PA6795UKMFR9' },
                                    { value: 'FR', label: 'France (FR)', marketplaceId: 'A13V1IB3VIYBER' },
                                    { value: 'IT', label: 'Italy (IT)', marketplaceId: 'APJ6JRA9NG5V4' },
                                    { value: 'ES', label: 'Spain (ES)', marketplaceId: 'A1RKKUPIHCS9HS' },
                                    { value: 'UK', label: 'United Kingdom (UK)', marketplaceId: 'A1F83G8C2ARO7P' },
                                    { value: 'SE', label: 'Sweden (SE)', marketplaceId: 'A2NODRKZP88ZB9' },
                                    { value: 'PL', label: 'Poland (PL)', marketplaceId: 'A1C3SOZRARQ6R3' },
                                    { value: 'IE', label: 'Ireland (IE)', marketplaceId: 'A28R8C7NBKEWEA' }
                                ].findIndex(option => option.value === (webshopCredentialsInfo?.marketplace || 'NL'))}
                                onChange={(selected) => {
                                    const marketplaceOptions = [
                                        { value: 'NL', label: 'Netherlands (NL)', marketplaceId: 'A1805IZSGTT6HS' },
                                        { value: 'BE', label: 'Belgium (BE)', marketplaceId: 'AMEN7PMS3EDWL' },
                                        { value: 'DE', label: 'Germany (DE)', marketplaceId: 'A1PA6795UKMFR9' },
                                        { value: 'FR', label: 'France (FR)', marketplaceId: 'A13V1IB3VIYBER' },
                                        { value: 'IT', label: 'Italy (IT)', marketplaceId: 'APJ6JRA9NG5V4' },
                                        { value: 'ES', label: 'Spain (ES)', marketplaceId: 'A1RKKUPIHCS9HS' },
                                        { value: 'UK', label: 'United Kingdom (UK)', marketplaceId: 'A1F83G8C2ARO7P' },
                                        { value: 'SE', label: 'Sweden (SE)', marketplaceId: 'A2NODRKZP88ZB9' },
                                        { value: 'PL', label: 'Poland (PL)', marketplaceId: 'A1C3SOZRARQ6R3' },
                                        { value: 'IE', label: 'Ireland (IE)', marketplaceId: 'A28R8C7NBKEWEA' }
                                    ];
                                    handleCredentialsChange('marketplace', marketplaceOptions[selected].value);
                                }}
                                closeOnSelection
                            />
                        </Box>
                    </Box>

                    {/* Informational subscription prices for amazon_stockhub (auto-created by backend) */}
                    <AmazonSubscriptionInfo />
                </Box>
            )}

            {/* TikTok Shop OAuth for tiktokshop type */}
            {data.webshop_type === 'tiktokshop' && (
                <Box sx={{ width: 1, mt: 2 }}>
                    {/* TikTok Region Dropdown */}
                    <Box
                        sx={{
                            width: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 7,
                            mb: 2
                        }}
                    >
                        <Box sx={{ width: 120 }}>
                            <Text>{t("region")}</Text>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                borderRadius: 4,
                                display: "flex"
                            }}
                        >
                            <InputDropdown
                                name="region"
                                noSelection={t('choose_option')}
                                displayName="label"
                                options={TIKTOK_REGION_OPTIONS}
                                selected={TIKTOK_REGION_OPTIONS.findIndex(option => option.value === (webshopCredentialsInfo?.region || 'global'))}
                                onChange={(selected) => {
                                    handleCredentialsChange('region', TIKTOK_REGION_OPTIONS[selected].value);
                                }}
                                closeOnSelection
                            />
                        </Box>
                    </Box>

                    <TikTokOAuthButton
                        oauthUrl={tiktokOAuthUrl}
                        backendOrigin={tiktokBackendOrigin}
                        region={webshopCredentialsInfo?.region || 'global'}
                        onSuccess={handleTikTokOAuthSuccess}
                        onError={handleTikTokOAuthError}
                        isAuthenticated={isOAuthAuthenticated}
                        onDisconnect={handleTikTokOAuthDisconnect}
                    />

                    {/* Display OAuth error if any */}
                    {oauthError && (
                        <Box sx={{
                            mt: 1.5,
                            p: 1.5,
                            backgroundColor: '#ffebee',
                            borderRadius: 1,
                            border: '1px solid #f44336'
                        }}>
                            <Text variant="body2" color="error">
                                {oauthError}
                            </Text>
                        </Box>
                    )}

                    {/* Shop picker when the token authorizes multiple shops */}
                    {tiktokOAuthPayload && (
                        <Box
                            sx={{
                                width: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 7,
                                mt: 2
                            }}
                        >
                            <Box sx={{ width: 120 }}>
                                <Text>{t("tiktok_shop")}</Text>
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                    borderRadius: 4,
                                    display: "flex"
                                }}
                            >
                                <InputDropdown
                                    name="tiktok_shop"
                                    noSelection={t('choose_option')}
                                    displayName="label"
                                    options={tiktokOAuthPayload.shops.map(shop => ({
                                        label: shop.shop_name ? `${shop.shop_name} (${shop.shop_id})` : shop.shop_id,
                                    }))}
                                    selected={tiktokOAuthPayload.shops.findIndex(shop => shop.shop_id === webshopCredentialsInfo?.shop_id)}
                                    onChange={handleTikTokShopPick}
                                    closeOnSelection
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {Object.keys(webshopCredentialsInfo)
            .filter(key => {
                // Always filter out measuring_units
                if (key === "measuring_units") return false;

                // For kaufland, filter out storefront (handled separately with dropdown)
                if (data.webshop_type === 'kaufland' && key === 'storefront') {
                    return false;
                }

                // For mirakl, the url is only typed in for marketplaces missing from the dropdown
                if (data.webshop_type === 'mirakl' && key === 'url') {
                    return miraklOther;
                }

                if (data.webshop_type === 'amazon_stockhub') {
                    const oauthManagedFields = [
                        'refresh_token',
                        'selling_partner_id',
                        'marketplace',
                        'marketplace_id',
                    ];
                    return !oauthManagedFields.includes(key);
                }

                if (data.webshop_type === 'tiktokshop') {
                    return !TIKTOK_OAUTH_MANAGED_FIELDS.includes(key);
                }

                return true;
            })
            .map((key) => {
                const fieldExplainer = CREDENTIAL_FIELD_EXPLAINERS[data.webshop_type]?.[key];
                return (
                <Box
                    key={key}
                    sx={{
                        width: 1,
                        paddingTop: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Text>{t(key)}</Text>
                        {fieldExplainer && (
                            <EMGeneralExplainerBox
                                title={t(fieldExplainer.title)}
                                text={t(fieldExplainer.text)}
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            //backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4,
                            display: "flex"
                        }}
                    >
                        {key.includes('_bool') || key === 'syncOrders' || key === 'syncStock' ? (
                            <ToggleButtonGroup
                                color={webshopCredentialsInfo[key] === true || webshopCredentialsInfo[key] === 'true' ? "green" : "red"}
                                value={webshopCredentialsInfo[key] === true || webshopCredentialsInfo[key] === 'true' ? "true" : "false"}
                                exclusive
                                onChange={(e,value) => handleCredentialsChange(key, value)}
                                aria-label="Active"
                                className={cx(`${webshopCredentialsInfo[key]}`, {
                                    'active': webshopCredentialsInfo[key] === true || webshopCredentialsInfo[key] === 'true'
                                })}
                                sx={{
                                    background: colors.red[100],
                                    padding:0.5,
                                    borderRadius:5,
                                    transition:"250ms",
                                    "&.active" : {
                                        background:colors.green[100],
                                        transition:"250ms",
                                    },
                                }}
                            >
                                <ToggleButton 
                                    sx={{
                                        padding:0,
                                        paddingX:2,
                                        border:0,
                                        lineHeight:"32px",
                                        textTransform:"unset",
                                        borderRadius:"32px !important",
                                        color:colors.green[400],
                                        transition:"250ms",
                                        "&.Mui-selected,&.Mui-selected:hover" : {
                                            background:colors.red[400],
                                            color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                        }
                                    }}
                                    value="false">
                                    {t('inactive')}
                                </ToggleButton>
                                <ToggleButton 
                                    sx={{
                                        padding:0,
                                        paddingX:2,
                                        border:0,
                                        lineHeight:"32px",
                                        textTransform:"unset",
                                        borderRadius:"32px !important",
                                        color:colors.red[400],
                                        transition:"250ms",
                                        "&.Mui-selected,&.Mui-selected:hover" : {
                                            background:colors.green[400],
                                            color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                        }
                                    }}
                                    value="true"
                                >
                                    {t('active')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        ) : (
                            <ShValidatedInput
                                name={key}
                                value={webshopCredentialsInfo[key] ?? ""}
                                onChange={handleCredentialsChange}
                                error={props.errors[`credentials.${key}`]}
                            />
                        )}
                    </Box>
                </Box>
                );
            })}
            {/* Hide test connection for shopify and amazon_stockhub */}
            {data.webshop_type === "shopify" || data.webshop_type === "amazon_stockhub" || data.webshop_type === "bol" ? <></> :
            <Box sx={{ display:"flex", mt:2, gap: 2 }}>
                <Shbutton
                    sx={{
                        maxWidth: 200
                    }}
                    isLoading={testConnectionLoading}
                    className={"group-btn"}
                    color={"grey"}
                    onClick={() => handleTestConnection()}
                    variant="contained"
                    tabindex={1}
                >
                    {t('test_connection')}
                </Shbutton>

                {(
                    testConnectionLoading ?
                        <ShLabel variant="blue" palette="normal" fitted>{t('connecting')}</ShLabel>
                    : connectionTestResult === false ?
                        <ShLabel variant="red" palette="normal" fitted>{t('connection_failed')}</ShLabel>
                    : connectionTestResult === true ?
                        <ShLabel variant="green" palette="normal" fitted>{t('connected')}</ShLabel>
                        : <></>
                )}
            </Box>
            }
    </div>
    );
};

const Credentials = (props) => {
    const { t } = useTranslation();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    // amazon_stockhub appends the subscription price block, which overflows the default
    // tab height — give that step a little more room so it doesn't scroll.
    const webshopType = useSelector((state) => state.progressiontabs.data?.webshop_type);
    const isAmazon = webshopType === 'amazon_stockhub';
    const isTikTok = webshopType === 'tiktokshop';

    // TikTok credentials only exist after the OAuth flow completed and a shop was
    // picked; block proceeding until then so no malformed credentials blob is posted.
    const wizardCredentials = useSelector((state) => state.progressiontabs.data?.credentials);
    const tiktokConnected = !!wizardCredentials?.access_token && !!wizardCredentials?.shop_cipher;

    // Block proceeding past this step for amazon_stockhub until the fulfilment has set both
    // amazon prices (monthly + per order) for the relation's invoicecategory. The backend
    // enforces this too; this just gives immediate UX feedback.
    const { data: amazonPrices } = useGetSubscriptionPricesQuery('amazon', { skip: !isAmazon });
    const amazonPricesConfigured = !!amazonPrices
        && amazonPrices.monthly !== null && amazonPrices.monthly !== undefined
        && amazonPrices.order !== null && amazonPrices.order !== undefined;

    // Create a mapping of fields to their webshop types
    const FIELD_TO_WEBSHOPS = {
        'url': ['woocommerce', 'ccvshop', 'shopware6', 'mirakl'],
        'consumer_key': ['woocommerce'],
        'consumer_secret': ['woocommerce'],
        'shop': [ 'magento'],
        'client_key': ['kaufland'],
        'access_token': ['magento', 'plugandpay'],//['shopify', 'magento'],
        'client_id': ['shopware6'],
        'client_secret': ['kaufland', 'shopware6'],
        'public_key': ['ccvshop'],
        'secret_key': ['ccvshop'],
        'platform': ['etailize'],
        'api_key': ['lightspeed_ecom', 'etailize', 'mirakl'],
        'api_secret': ['lightspeed_ecom', 'etailize'],
        'syncOrders': ['etailize'],
        'syncStock': ['etailize'],
        'storefront': ['kaufland'],
        'shop_id': ['mirakl']
    };

    // Extra per-field validation, keyed by field name. Runs on top of the required check.
    const FIELD_VALIDATORS = {
        // Mirakl API endpoints must point at the /api path.
        'url': (value, data) =>
            data.webshop_type === 'mirakl' && value && !value.endsWith('/api')
                ? t('validation_url_must_end_with_api')
                : undefined
    };

    // Create conditional schema
    const conditionalSchema = [
        {
            key: "description",
            errorField: t('description'),
            alwaysRequired: true,
            type: "text",
        },
        {
            key: "relationId",
            errorField: t('relation'),
            alwaysRequired: isFulfilment, // Always required if isFulfilment is true
            condition: (data) => false, // If not alwaysRequired, return false
            type: "integer",
        },
        // Generate schema based on field-to-webshops mapping
        ...Object.entries(FIELD_TO_WEBSHOPS).map(([field, webshopTypes]) => ({
            key: `credentials.${field}`,  // Add credentials prefix
            errorField: t(field),
            alwaysRequired: false,
            condition: (data) => webshopTypes.includes(data.webshop_type),
            type: "text",
            ...(FIELD_VALIDATORS[field] ? { validate: FIELD_VALIDATORS[field] } : {})
        }))
    ];

    return (
        <ProgressionTab
            tabId="tabCredentials"
            subtitle={t("webshopcredentials")}
            step={2}
            nextTab={"tabSync"}
            maxSteps={3}
            conditionalSchema={conditionalSchema}
            nextDisabled={(isAmazon && !amazonPricesConfigured) || (isTikTok && !tiktokConnected)}
            sx={isAmazon ? { body: { maxHeight: 560 } } : undefined}
        >
            <Content />
        </ProgressionTab>
    )
}

export default Credentials;

